import { useState } from "react";
import { Upload, Trash2, Loader2, FileText, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { viewDataUrl } from "../../lib/file";

const inputCls = "w-full bg-white border border-line focus:border-gold outline-none px-4 py-2.5 rounded-sm text-sm focus:ring-2 focus:ring-gold/30";
const labelCls = "block text-xs uppercase tracking-widest text-forest/60 mb-1.5";

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function SyllabusManager() {
  const { syllabus, reload } = useSite();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ title: "", category: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm({ title: "", category: "" }); setFile(null); setModal(true); };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) { toast.error("File too large (max 12MB)"); e.target.value = ""; return; }
    setFile(f);
    if (!form.title) setForm((prev) => ({ ...prev, title: f.name.replace(/\.[^.]+$/, "") }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose a PDF or image file"); return; }
    setSaving(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      const type = file.type.includes("pdf") ? "pdf" : file.type.startsWith("image/") ? "image" : "file";
      await api.create("syllabus", { title: form.title || file.name, category: form.category || "General", file: dataUrl, file_name: file.name, file_type: type });
      toast.success("Syllabus uploaded");
      setModal(false);
      await reload();
    } catch { toast.error("Upload failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.remove("syllabus", id); toast.success("Deleted"); await reload(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid="syllabus-manager">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="font-serif text-2xl text-forest">{syllabus.length} Syllabus File{syllabus.length !== 1 ? "s" : ""}</h2>
          <p className="text-muted text-sm mt-1">Upload PDF or image syllabus files. Students can view and download them.</p>
        </div>
        <button data-testid="add-syllabus-btn" onClick={openNew} className="inline-flex items-center gap-2 bg-gold text-forest px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform">
          <Upload size={16} /> Upload Syllabus
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {syllabus.map((s) => (
          <div key={s.id} data-testid="syllabus-admin-item" className="bg-white border border-line rounded-sm p-5 flex flex-col">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-sm bg-forest/5 text-forest flex items-center justify-center shrink-0">
                {s.file_type === "image" ? <ImageIcon size={18} /> : <FileText size={18} />}
              </span>
              <div className="min-w-0">
                <h3 className="font-serif text-lg text-forest leading-tight">{s.title}</h3>
                <span className="text-[10px] uppercase tracking-widest text-gold">{s.category}</span>
              </div>
            </div>
            <p className="text-xs text-muted mt-3 truncate">{s.file_name}</p>
            <div className="flex items-center gap-2 mt-4">
              <button type="button" data-testid="preview-syllabus-btn" onClick={() => viewDataUrl(s.file)} className="text-xs text-forest underline">Preview</button>
              <button data-testid="delete-syllabus-btn" onClick={() => remove(s.id)} className="ml-auto inline-flex items-center gap-1.5 text-xs text-destructive hover:underline">
                <Trash2 size={13} /> Delete
              </button>
            </div>
          </div>
        ))}
        {syllabus.length === 0 && <p className="p-8 text-muted col-span-full">No syllabus files yet. Upload one above.</p>}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/60 backdrop-blur-sm" onClick={() => setModal(false)}>
          <form data-testid="syllabus-form" onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-bone w-full max-w-lg rounded-sm border border-line p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-3xl text-forest">Upload Syllabus</h3>
              <button type="button" onClick={() => setModal(false)} className="text-muted hover:text-forest"><X size={22} /></button>
            </div>
            <div className="mb-5">
              <label className={labelCls}>Title</label>
              <input data-testid="syllabus-field-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className={inputCls} placeholder="e.g. B.A. English Syllabus" />
            </div>
            <div className="mb-5">
              <label className={labelCls}>Category / Department</label>
              <input data-testid="syllabus-field-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} placeholder="e.g. English, General" />
            </div>
            <div className="mb-6">
              <label className={labelCls}>File (PDF or image, max 12MB)</label>
              <input data-testid="syllabus-field-file" type="file" accept=".pdf,image/*" onChange={onPick} className="block w-full text-sm text-muted file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-forest file:text-bone file:text-xs file:uppercase file:tracking-widest" />
              {file && <p className="text-xs text-forest mt-2">{file.name}</p>}
            </div>
            <button data-testid="save-syllabus-btn" type="submit" disabled={saving} className="w-full bg-forest text-bone py-4 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-60">
              {saving ? "Uploading…" : "Upload"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
